/**
 * Centralized date utilities.
 * Re-exports commonly used date-fns functions and provides custom formatters.
 * All date-fns usage in modules should import from here.
 */

// ─── date-fns re-exports ────────────────────────────────────────────────────
export {
  format,
  formatDistanceToNow,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  parseISO,
  isToday,
  isYesterday,
  subWeeks,
  addMonths,
  subMonths,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
} from 'date-fns'

// ─── Custom date formatters ─────────────────────────────────────────────────

/** Format a date for display: "Mar 15, 2026". Returns '-' for null/undefined. */
export function formatDate(date: Date | string | number | null | undefined): string {
  if (!date) return '-'
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** Format a date with time: "Mar 15, 2026, 3:45 PM". */
export function formatDateTime(timestamp: number | string): string {
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Format seconds as "Xh Ym". */
export function formatDuration(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds < 0) return '0m'
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  if (hours === 0) return `${minutes}m`
  if (minutes === 0) return `${hours}h`
  return `${hours}h ${minutes}m`
}

/** Format minutes as "Xh Ym". */
export function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h > 0 && m > 0) return `${h}h ${m}m`
  if (h > 0) return `${h}h`
  return `${m}m`
}
